interface Segment {
  label: string;
  value: number;
  color: string;
}

interface DonutChartProps {
  segments: Segment[];
  size?: number;
  strokeWidth?: number;
  centerLabel?: string;
  centerValue?: string | number;
}

export default function DonutChart({
  segments,
  size = 140,
  strokeWidth = 22,
  centerLabel,
  centerValue,
}: DonutChartProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  const total = segments.reduce((sum, s) => sum + s.value, 0);

  if (total === 0) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={strokeWidth}
        />
        {centerLabel && (
          <text
            x={center}
            y={center + 4}
            textAnchor="middle"
            className="fill-slate-400 text-xs"
          >
            Sem dados
          </text>
        )}
      </svg>
    );
  }

  let offset = 0;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0"
      role="img"
      aria-label="Gráfico de distribuição"
    >
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="#f1f5f9"
        strokeWidth={strokeWidth}
      />

      {segments
        .filter((s) => s.value > 0)
        .map((segment) => {
          const pct = segment.value / total;
          const dashLength = pct * circumference;
          const gapLength = circumference - dashLength;
          const currentOffset = offset;
          offset += pct * circumference;

          return (
            <circle
              key={segment.label}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dashLength} ${gapLength}`}
              strokeDashoffset={-currentOffset}
              strokeLinecap="round"
              className="transition-all duration-700 ease-out"
              style={{
                transform: 'rotate(-90deg)',
                transformOrigin: `${center}px ${center}px`,
              }}
            >
              <title>{segment.label}: {segment.value} ({Math.round(pct * 100)}%)</title>
            </circle>
          );
        })}

      {(centerValue !== undefined || centerLabel) && (
        <>
          {centerValue !== undefined && (
            <text
              x={center}
              y={center - 2}
              textAnchor="middle"
              dominantBaseline="central"
              className="fill-slate-800 font-bold"
              style={{ fontSize: size * 0.17 }}
            >
              {centerValue}
            </text>
          )}
          {centerLabel && (
            <text
              x={center}
              y={center + size * 0.12}
              textAnchor="middle"
              dominantBaseline="central"
              className="fill-slate-400"
              style={{ fontSize: size * 0.075 }}
            >
              {centerLabel}
            </text>
          )}
        </>
      )}
    </svg>
  );
}
